import type { ReactNode } from 'react';

import { Show } from '@dvb/ui';
import { Spinner } from '@heroui/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from 'usehooks-ts';

interface LoadMoreProps {
  fetchNextPage: () => void;
  hasNextPage: boolean;
  icon?: ReactNode;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  loadingText?: string;
  noMoreText?: string;
}

export function LoadMore({
  fetchNextPage,
  hasNextPage,
  icon = <Spinner size="sm" />,
  isFetchingNextPage,
  isLoading,
  loadingText = '',
  noMoreText = '',
}: LoadMoreProps) {
  const { t } = useTranslation();

  const { isIntersecting, ref: intersectionRef } = useIntersectionObserver({
    threshold: 0.5,
  });

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting]);

  return (
    <div
      className="flex items-center justify-center gap-2"
      ref={intersectionRef}
    >
      <Show
        fallback={
          <span className="text-default-500">
            {noMoreText || t('noMoreData')}
          </span>
        }
        when={isLoading || isFetchingNextPage}
      >
        {icon}
        <span className="text-default-500">{loadingText || t('loading')}</span>
      </Show>
    </div>
  );
}
